import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import hasPermission from "../utils/hasMultiplePermission";
import { useHistory } from "react-router-dom";

const defaultDependencies = [];
const defaultCondition = () => true;

/**
 * Handy custom hook to check if a user has access to a permission
 * @param {Array} params.scopes - Required scopes to check permission against.
 * @param {Function} params.onNoAccess - Callback if access denied
 * @param {Function} [params.onHasAccess] - Optional callback if access granted
 * @param {Array} [params.dependencies] - Optional Recompute if dependency list changes
 * @returns {boolean | null}
 *  null    - permissions not loaded yet (loading state)
 *  true    - access granted
 *  false   - access denied
 */
export const usePermissionGuard = ({ onNoAccess, onHasAccess, scopes, dependencies = defaultDependencies }) => {
    const givenPermissions = useSelector((state) => state.givenPermission);
    const scopeDeps = useMemo(() => {
        return scopes.join(",");
    }, [scopes]);

    const access = useMemo(() => {
        if (!givenPermissions) {
            return null;
        }

        const permissionsObject = givenPermissions?.givenPermission ?? givenPermissions;

        if (
            !permissionsObject ||
            typeof permissionsObject !== "object" ||
            Object.keys(permissionsObject).length === 0
        ) {
            return null;
        }
        const permission = hasPermission({
            scopes,
            permissions: permissionsObject
        });
        return permission;
    }, [givenPermissions, scopeDeps, ...dependencies]);

    useEffect(() => {
        if (access === null) {
            return;
        }

        if (!access) {
            if (onNoAccess) {
                onNoAccess();
            }
        } else {
            if (onHasAccess) {
                onHasAccess();
            }
        }
    }, [access]);

    return access;
};

/**
 * Redirect if the user doesn't have permission to view some page.
 * @param {string[]} scopes - permission scopes to check
 * @param {Function} noAccessRedirectCondition - optional predicate to decide whether to redirect
 * @returns {boolean|undefined} access
 */
export const usePermissionGuardRedirect = (scopes, noAccessRedirectCondition = defaultCondition) => {
    const history = useHistory();

    const onNoAccess = () => {
        if (noAccessRedirectCondition()) {
            history.push('/noaccess');
        }
    };

    const access = usePermissionGuard({
        scopes,
        onNoAccess
    });

    return access;
};
